import { UserRole } from '@/types';
import { EApprovalPriority, IApprovalRequest } from '@/types/v2';
import { jwtDecode } from 'jwt-decode';
import { ITimeRange } from 'moh-ui';

export const formatUnderScoreText = (text: string): string => {
   return text
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
};

/**
 * @param str
 * @returns a capitalize string ex: 'HELLO_WORLD' => 'Hello World'
 */
export const capitalize = (str: string): string => {
   return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
};
/**
 * @param error
 * @returns  a string error from response
 */
export const getResError = (error?: any) => {
   if (!error) return 'Something Went Wrong';
   const isNetError = error?.message?.includes('Network Error');
   if (isNetError) return 'Network Error';
   return error?.response?.data?.error ?? error?.response?.data?.message ?? error?.message ?? 'Something Went Wrong';
};

/**
 * Convert image file to base64 string
 * @param file
 * @returns a promise that resolves with the base64 string (if not errors occured)
 */
export const imageFileToBase64 = (file: File): Promise<string | null> => {
   return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
   });
};

export const decode = (token: string) => {
   try {
      const data = jwtDecode(token);
      return data;
   } catch (error) {
      return null;
   }
};
export const getUseRoute = (role: UserRole) => {
   switch (role) {
      case UserRole.ADMIN:
         return '/moh-admin';
      case UserRole.INSURANCE_ADMIN:
         return '/insurance';
      default:
         return '/';
   }
};

/**
 * function that takes a key string like 'name' or 'name.first' and returns a function that takes an object and returns the value of the key in the object
 * @param key - the key to get the value of
 * @param obj - the object to get the value from
 */
export const getObjValue = (key: string | number, obj: any) => {
   const keys = key.toString().split('.');
   let result = obj;
   for (const key of keys) {
      if (result && Object.prototype.hasOwnProperty.call(result, key)) {
         result = result[key];
      } else {
         return undefined;
      }
   }
   return result as string;
};

/**
 * Removes null, undefined, or empty keys from an object
 * @param obj Object to clean
 * @param options Configuration options
 * @returns New object with filtered keys
 */
export const removeEmptyKeys = <T extends Record<string, any>>(
   obj: T,
   options: {
      removeNull?: boolean; // Remove null and undefined values
      removeEmpty?: boolean; // Remove empty strings
      removeEmptyArrays?: boolean; // Remove empty arrays
      removeEmptyObjects?: boolean; // Remove empty objects
   } = { removeNull: true, removeEmpty: false, removeEmptyArrays: false, removeEmptyObjects: false },
): Partial<T> => {
   const { removeNull, removeEmpty, removeEmptyArrays, removeEmptyObjects } = options;

   return Object.entries(obj).reduce((acc, [key, value]) => {
      // Skip null/undefined values if configured
      if (removeNull && (value === null || value === undefined)) {
         return acc;
      }

      // Skip empty strings if configured
      if (removeEmpty && value === '') {
         return acc;
      }

      // Skip empty arrays if configured
      if (removeEmptyArrays && Array.isArray(value) && value.length === 0) {
         return acc;
      }

      // Skip empty objects if configured
      if (
         removeEmptyObjects &&
         typeof value === 'object' &&
         value !== null &&
         !Array.isArray(value) &&
         Object.keys(value).length === 0
      ) {
         return acc;
      }

      // Keep all other values
      return { ...acc, [key]: value };
   }, {} as Partial<T>);
};

/**
 * Builds query parameters string from an object with advanced options
 * @param params Object containing query parameters
 * @param options Configuration options
 * @returns Formatted query string (without the leading '?')
 */
export const buildQueryParams = (
   params: Record<string, any>,
   options: {
      skipNull?: boolean; // Skip null and undefined values
      skipEmpty?: boolean; // Skip empty strings
      arrayFormat?: 'comma' | 'bracket' | 'repeat'; // How to format array values
   } = { skipNull: true, skipEmpty: true, arrayFormat: 'repeat' },
) => {
   const { skipNull, skipEmpty, arrayFormat } = options;

   // Remove empty keys based on options
   params = removeEmptyKeys(params, {
      removeNull: skipNull,
      removeEmpty: skipEmpty,
   });
   return Object.entries(params)
      .filter(([_, value]) => {
         if (skipNull && (value === null || value === undefined)) return false;
         if (skipEmpty && value === '') return false;
         return true;
      })
      .map(([key, value]) => {
         // Handle arrays based on specified format
         if (Array.isArray(value)) {
            if (arrayFormat === 'comma') {
               return `${encodeURIComponent(key)}=${value.map(encodeURIComponent).join(',')}`;
            } else if (arrayFormat === 'bracket') {
               return `${encodeURIComponent(key)}[]=${value.map(encodeURIComponent).join('&' + encodeURIComponent(key) + '[]=')}`;
            } else {
               // repeat (default)
               return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
            }
         }

         // Handle objects by converting to JSON string
         if (value !== null && typeof value === 'object') {
            return `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`;
         }

         // Handle primitive values
         return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      })
      .join('&');
};

export const appendFilters = (baseParams: Record<string, any>, filters: Record<string, any>) => {
   return { ...baseParams, ...filters };
};

export const sortApprovalRequests = (approvalRequests: IApprovalRequest[]) => {
   return [...approvalRequests].sort((a, b) => {
      // First prioritize PENDING status
      if (a.status === 'PENDING' && b.status !== 'PENDING') return -1;
      if (b.status === 'PENDING' && a.status !== 'PENDING') return 1;

      // If both have the same status, prioritize by priority level
      if (a.status === b.status) {
         // HIGH priority comes first
         if (a.priority === EApprovalPriority.HIGH && b.priority !== EApprovalPriority.HIGH) return -1;
         if (b.priority === EApprovalPriority.HIGH && a.priority !== EApprovalPriority.HIGH) return 1;

         // Then MEDIUM priority
         if (a.priority === EApprovalPriority.MEDIUM && b.priority === EApprovalPriority.LOW) return -1;
         if (b.priority === EApprovalPriority.MEDIUM && a.priority === EApprovalPriority.LOW) return 1;
      }

      // Then sort by date (most recent on top)
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
   });
};

export const DEFAULT_TIME_RANGE: ITimeRange = {
   startDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
   endDate: new Date(),
};

export const formatDate = (date: Date | undefined): string | undefined => {
   // Extract date components
   if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      // Extract time components
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

      // Combine date and time with desired separator
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
   } else return undefined;
};

/***
 * convert object to query params
 * @param obj
 * @return string
 */
export const objectToQueryParams = (obj: { [key: string]: string | string[] | number | boolean | undefined }) => {
   const params = Object.entries(obj)
      .filter(([_, value]) => value === 0 || value === false || Boolean(value))
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value?.toString() || '')}`);

   return params.join('&');
};
